import React, { useState, useEffect } from 'react';
import EducationVisual from './EducationVisual';

const EducationModal = ({ 
  modalName = 'overview', 
  onComplete, 
  onClose,
  content = null // Allow custom content to be passed in
}) => {
  const [currentScreen, setCurrentScreen] = useState(0);
  
  // Default content - can be overridden by admin
  const defaultScreens = {
    overview: [
      {
        title: "The Food Cost Problem",
        icon: "📊",
        content: [
          "Did you know that 30-40% of the cost of the food you buy is due to the fact that it is trans-shipped and handled 5 to 6 times between the farm gate and your kitchen?",
          "",
          "Our Pickup sets out to reduce this waste as much as possible, meaning that the cost of getting food to the consumer is reduced while the profitability of the farmer is increased.",
          "",
          "We accomplish this objective by enabling people to use Our Pickup in several different roles:"
        ],
        visual: "farm-to-table"
      },
      {
        title: "Pickups - The Core Innovation",
        icon: "🚗",
        content: [
          "Pickups are at the core of Our Pickup. Our objective is to make much more use of the fleet of millions of vehicles used by consumers to go to supermarkets empty and return home with the food requirements for just one household.",
          "",
          "By becoming a Pickup, anyone with carrying capacity can be rewarded for delivering food from Producers and Hubs to their friends and neighbours.",
          "",
          "A pickup with a larger vehicle or refrigerated van can earn an income by transporting produce from Producers to Hubs or to larger Buyers such as restaurants, caterers or function rooms."
        ],
        visual: "pickup-delivery"
      },
      {
        title: "Producers - Farm to Table",
        icon: "🚜",
        content: [
          "Producers run farms where they grow vegetable and fruit crops, rear stock for meat and other products like milk, or produce cheese, jams, wine, beer, honey or other products which are agricultural in nature.",
          "",
          "Our Pickup introduces Producers to consumers in their local area and helps them to buy directly.",
          "",
          "We have also created Pickup Pay, an innovative online payment method for bank account to account transfer which reduces Producer selling costs substantially from the current 5%.",
          "",
          "This 5% includes fees charged by Stripe, Shopify and other online platforms plus the cost of setting up and maintaining a web site."
        ],
        visual: "producer-farm"
      },
      {
        title: "Hubs - Aggregating Local Food",
        icon: "🏪",
        content: [
          "Hubs source products from multiple local farms and are often producers themselves. Because they aggregate a variety of foods under one roof, they reduce transport costs for consumers.",
          "",
          "Our Pickup accommodates Hubs selling products on consignment from Producers, allowing them to easily set up commission arrangements for each individual item.",
          "",
          "Hubs can also buy directly from Producers with payment arrangements made possible by Pickup Pay.",
          "",
          "Vendors typically pay 10-20% of their costs to get their product to market. Our Pickup significantly reduces this through direct sales and community-based delivery."
        ],
        visual: "hub-warehouse"
      },
      {
        title: "Savings & Opportunities for Everyone",
        icon: "💰",
        content: [
          "Lower logistics and finance charges enable cost savings to food Buyers. Not only can they purchase their food requirements substantially cheaper than supermarket prices, but we offer additional opportunities to save money.",
          "",
          "Individual Buyers can also become Pickups where they can earn food credits by making deliveries from Producers and Hubs to other Our Pickup buyers close to them.",
          "",
          "Producers and Hubs can offer food credits to Buyers as a reward for introducing new Buyers.",
          "",
          "Groups of Buyers who live close to each other can form a Kin which is a self managed buying group who can get discounts by ordering in greater volumes.",
          "",
          "Producers and Hubs can bid for the business of Kins by creating pricing tiers where they incentivise volume purchases. Typically a Kin will include its own Pickups so that vendors don't incur delivery costs."
        ],
        visual: "savings"
      }
    ],
    // Other role modals will be added here
  };

  // Use provided content or default
  const screens = content || defaultScreens[modalName] || defaultScreens.overview;
  const current = screens[currentScreen];
  const isLast = currentScreen === screens.length - 1;
  const isFirst = currentScreen === 0;

  // Track engagement
  useEffect(() => {
    trackModalAction('open');
    return () => {
      // Track time spent on close
      if (startTime) {
        const seconds = Math.round((Date.now() - startTime) / 1000);
        if (seconds > 1) {
          trackModalAction('time', { seconds });
        }
      }
    };
  }, []);

  const startTime = Date.now();

  const trackModalAction = async (action, data = {}) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      await fetch(`/api/engagement/modal/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          modalName: modalName,
          ...data
        })
      });
    } catch (err) {
      console.error('Failed to track engagement:', err);
    }
  };

  const handleNext = () => {
    const nextPage = currentScreen + 1;
    if (!isLast) {
      trackModalAction('page', { pageNumber: nextPage });
      setCurrentScreen(nextPage);
    } else {
      trackModalAction('complete');
      onComplete();
    }
  };

  const handleBack = () => {
    if (!isFirst) {
      setCurrentScreen(currentScreen - 1);
    }
  };

  const handleSkip = () => {
    if (window.confirm('Are you sure you want to skip this introduction? You can always access it later.')) {
      onComplete();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Progress bar */}
        <div className="px-6 pt-6 pb-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">
              Step {currentScreen + 1} of {screens.length}
            </span>
            <button
              onClick={handleSkip}
              className="text-sm text-gray-400 hover:text-gray-600"
            >
              Skip introduction
            </button>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentScreen + 1) / screens.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <span className="text-5xl block mb-3">{current.icon}</span>
            <h2 className="text-2xl font-bold text-gray-800">{current.title}</h2>
          </div>

          <div className="space-y-3 text-gray-600 leading-relaxed">
            {current.content.map((paragraph, idx) => {
              if (paragraph === '') {
                return <div key={idx} className="h-3" />;
              }
              return <p key={idx}>{paragraph}</p>;
            })}
          </div>

          {/* Visual */}
          <div className="mt-6">
            <EducationVisual type={current.visual} />
          </div>
        </div>

        {/* Navigation */}
        <div className="px-6 pb-6 pt-4 border-t border-gray-100 flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={isFirst}
            className={`px-4 py-2 rounded-lg ${
              isFirst
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            ← Back
          </button>

          <div className="flex items-center gap-2">
            {!isLast && (
              <button
                onClick={handleNext}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
              >
                Next →
              </button>
            )}
            {isLast && (
              <button
                onClick={handleNext}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2"
              >
                <span>🚀</span> Get Started
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationModal;
